import { useEffect, useState } from 'react';
import { AccordionDetails, useTheme } from '@mui/material';
import { Grid, Typography, Checkbox, Accordion } from '@ts/storybook';
import { NewPaperWithPadding } from '@components/Misc';
import { IForm } from '@ts/ui-models';

interface TrackingTabProps extends IForm {}

const TrackingTab = ({ values, mode, onChange, onCallbackChange }: TrackingTabProps) => {
  const [isAnyCompundItemChecked, setIsAnyCompundItemChecked] = useState(false);
  const [isAnyLiveLocationChecked, setIsAnyLiveLocationChecked] = useState(false);
  const [isAnyCalculatedPositionsChecked, setIsAnyCalculatedPositionsChecked] = useState(false);
  const [isAnyPositionInheritanceChecked, setIsAnyPositionInheritanceChecked] = useState(false);

  useEffect(() => {
    setIsAnyCompundItemChecked(values.isCompound);
    setIsAnyLiveLocationChecked(values.liveLocation || values.allowMultiLocation || values.allowLiveMultiLocation);
    setIsAnyCalculatedPositionsChecked(values.typeParams?.isSyntheticEnabled || values.typeParams?.allowsSynthetic);
    setIsAnyPositionInheritanceChecked(
      values.typeParams?.inheritCurrentLocations || values.typeParams?.inheritLocationCoordinates
    );
  }, [values]);

  const theme = useTheme();
  const disabled = mode === 'view';

  const onChangeParameterOfTypeParams = (key: string) => (value: boolean) => {
    onChange(`typeParams.${key}`, value);
  };

  //#region Render
  const renderCompoundItem = () => {
    return (
      <NewPaperWithPadding withoutScroll>
        <Accordion
          open={isAnyCompundItemChecked}
          title={
            <Grid container size={12} direction="column" spacing={2}>
              <Typography sx={{ color: theme.palette.text.primary }} variant="h6" text="Compound Item (Indivisible Unit)" />
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.pxToRem(15),
                }}
                text="Enable this option to treat this item and all its children as a single, indivisible entity. Actions like scanning, moving, or updating any part of the compound will apply to the whole group."
              />
            </Grid>
          }
        >
          <AccordionDetails className="flex flex-col p-4" sx={{ backgroundColor: theme.palette.background.default }}>
            <Checkbox
              label="Treat as Compound Item"
              isReadOnly={disabled}
              checked={values.isCompound}
              onChangeValue={onCallbackChange('isCompound')}
              stProps={{
                tooltip: {
                  text: 'When enabled, this item becomes a “Compound Item.” Any RFID scan of the parent or its children will reference the whole unit. Location changes or operations apply to all parts together.',
                },
              }}
            />
          </AccordionDetails>
        </Accordion>
      </NewPaperWithPadding>
    );
  };

  const renderLocationConfiguration = () => {
    return (
      <NewPaperWithPadding withoutScroll>
        <Accordion
          open={isAnyLiveLocationChecked}
          title={
            <Grid container size={12} direction="column" spacing={2}>
              <Typography sx={{ color: theme.palette.text.primary }} variant="h6" text="Live item locations update" />
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.pxToRem(15),
                }}
                text="Configure the live updating of item locations using inbound messages, ensuring that the current locations of items stay synchronized based on the readings received"
              />
            </Grid>
          }
        >
          <AccordionDetails className="flex flex-col p-4" sx={{ backgroundColor: theme.palette.background.default }}>
            <Checkbox
              label="Allow multiple locations"
              isReadOnly={disabled}
              checked={values.allowMultiLocation ?? true}
              onChangeValue={onCallbackChange('allowMultiLocation')}
              stProps={{
                tooltip: {
                  text: 'Enable the assignment of multiple locations to items',
                },
              }}
            />
            <Checkbox
              isReadOnly={disabled}
              label="Enable live update of location"
              checked={values.liveLocation ?? true}
              onChangeValue={onCallbackChange('liveLocation')}
              stProps={{
                tooltip: {
                  text: 'Receive live updates of current locations via readings of type location from inbound messages, ensuring accurate and up-to-date information',
                },
              }}
            />
            <Checkbox
              isReadOnly={disabled}
              label="Allow live update with multi-location readings"
              checked={values.allowLiveMultiLocation ?? true}
              onChangeValue={onCallbackChange('allowLiveMultiLocation')}
              stProps={{
                tooltip: {
                  text: 'Enable the simultaneous update of multiple current locations through readings of type location from inbound messages, providing comprehensive and synchronized location information',
                },
              }}
            />
          </AccordionDetails>
        </Accordion>
      </NewPaperWithPadding>
    );
  };

  const renderCalculatedPositionsConfiguration = () => {
    return (
      <NewPaperWithPadding withoutScroll>
        <Accordion
          open={isAnyCalculatedPositionsChecked}
          title={
            <Grid container direction={'column'} spacing={2}>
              <Typography sx={{ color: theme.palette.text.primary }} variant="h6" text="Calculated positions" />
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.pxToRem(15),
                }}
                text="Customize the automatic calculation of item positions based on the positions of other items"
              />
            </Grid>
          }
        >
          <AccordionDetails className="flex flex-col p-4" sx={{ backgroundColor: theme.palette.background.default }}>
            <Checkbox
              id="is-synthetic-enabled"
              isReadOnly={disabled}
              label="Enable automatic assignment of calculated positions"
              checked={values.typeParams?.isSyntheticEnabled}
              onChangeValue={onChangeParameterOfTypeParams('isSyntheticEnabled')}
              stProps={{
                tooltip: {
                  text: `Automatically calculate and assign positions to items of this type based on the positions of other items.
                  The calculation will depend of the position generation policy configured for the reference items`,
                },
              }}
            />
            <Checkbox
              id="allows-synthetic"
              isReadOnly={disabled}
              label="Use as position reference"
              //Add default value: true
              checked={values.typeParams?.allowsSynthetic}
              onChangeValue={onChangeParameterOfTypeParams('allowsSynthetic')}
              stProps={{
                tooltip: {
                  text: 'Allow items of this type to be used as references for calculating positions of other items',
                },
              }}
            />
          </AccordionDetails>
        </Accordion>
      </NewPaperWithPadding>
    );
  };

  const renderPositionInheritanceConfiguration = () => {
    return (
      <NewPaperWithPadding withoutScroll>
        <Accordion
          open={isAnyPositionInheritanceChecked}
          title={
            <Grid container direction="column" spacing={2}>
              <Typography sx={{ color: theme.palette.text.primary }} variant="h6" text="Location and position inheritance" />
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.pxToRem(15),
                }}
                text="Establish the inheritance of location and position attributes between parent and child items"
              />
            </Grid>
          }
        >
          <AccordionDetails className="flex flex-col p-4" sx={{ backgroundColor: theme.palette.background.default }}>
            <Checkbox
              isReadOnly={disabled}
              label="Inherit current locations from parent items"
              checked={values.typeParams?.inheritCurrentLocations}
              onChangeValue={onChangeParameterOfTypeParams('inheritCurrentLocations')}
              stProps={{
                tooltip: {
                  text: 'Enable items to inherit the current locations of their parent items once they are updated',
                },
              }}
            />
            <Checkbox
              isReadOnly={disabled}
              label="Inherit location coordinates from parent items"
              checked={values.typeParams?.inheritLocationCoordinates}
              onChangeValue={onChangeParameterOfTypeParams('inheritLocationCoordinates')}
              stProps={{
                tooltip: {
                  text: 'Enable items to inherit the location coordinates of their parent items',
                },
              }}
            />
          </AccordionDetails>
        </Accordion>
      </NewPaperWithPadding>
    );
  };

  //#endregion

  return (
    <Grid container size={12} spacing={2}>
      {renderCompoundItem()}
      {renderLocationConfiguration()}
      {renderCalculatedPositionsConfiguration()}
      {renderPositionInheritanceConfiguration()}
    </Grid>
  );
};

export default TrackingTab;
